import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState, useMemo } from 'react';
import { json, useLoaderData, useNavigate, useParams, useLocation } from 'react-router-dom';
import { PrimaryButton } from '../components/Buttons/Buttons';
import { LogoutButton } from '../components/Buttons/LogoutButton';
import { UserInfoForm } from '../components/UserInfoForm';
import { UserNotesDataType } from './UserNotesView';
import EmailModal from '../components/EmailManagerModal';
import { AdminEmails, LanguagesAndPreferences, UserPreferences } from '../types/types';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { RoundedButton } from '../styles/CustomButtons';
import {
  useUserInfo,
  useUserPicture,
  useEmailVerified,
  useGroupUserRoles,
  useCanPrepareOnBehalf,
  useFeatureFlag,
  useToggleEhrModeMutation,
  useGetLanguagesAndPreferencesQuery,
  useGroupIds,
  useResendVerificationEmailMutation,
  useLazyGetAdminEmailsQuery,
  useCreateOrRetrieveCheckoutSessionMutation,
  useCreateCustomerPortalSessionMutation,
} from '@features/user';
import { useJotMeetAvailability } from '@features/telehealth';
import { JotMeetSettingsSection } from '@features/telehealth/components/jotmeet-settings';
import LanguageAndNotePreferencesForm from '../components/LanguageAndNotePreferencesForm';
import { API_BASE_URL } from '../services/APIService';
import { useUIState } from '../context/uiState';
import ProviderInformationView from '../components/Provider/ProviderInformationView';
import {
  AccountCircle,
  BugReport,
  CalendarMonth,
  ContentCopy,
  Description,
  Edit,
  Info,
  IntegrationInstructions,
  Lightbulb,
  Mic,
  Redeem,
  Security,
  Settings,
  Shield,
  ToggleOn,
  Videocam,
} from '@mui/icons-material';
import { trackEvent } from '../utils/analytics_utils';
import { useEntitlement } from '@features/entitlements';
import { useBundlingEnabled } from '@features/subscriptions';
import { initiateGoogleIntegration } from '../services/GoogleCalendarService';
import LayoutWrapper from '../components/layout/UILayout';
import { useSnackbar } from 'notistack';
import UserPreferencesForm from '../components/Forms/UserPreferences';
import DocumentPreferencesForm from '../components/Forms/DocumentPreferencesForm';
import CopyPastePreferencesForm from '../components/Forms/CopyPastePreferencesForm';
import RecordingPreferencesForm from '../components/Forms/RecordingPreferencesForm';
import {
  DEANumberManagement,
  EPrescriptionUserSection,
  JotRxSetupCallout,
} from '@features/eprescriptions';
import { StripeConnectStatus, StripeInvoicesTableCard } from '@features/payments';
import {
  AddOnsSection,
  CurrentPlanCard,
  EntitlementsSection,
  useMyAccess,
} from '@features/subscriptions';
import { ReferralDashboard } from '@features/referrals';
import { EhrExtensionCard } from '@features/integrations/components/EhrExtensionCard';
import { FeatureGates, useFeatureGate } from '@services/statsig';

// Component prop interfaces
interface ProfileSectionProps {
  userInfo: any;
  emailVerified: boolean;
  resendVerificationEmail: () => Promise<void>;
  resendingEmail: boolean;
  userPicture?: string;
  isJotRxEnabled: boolean;
  isProxy: boolean;
}

interface IntegrationsSectionProps {
  loading: boolean;
  googleCalendarFeatureFlag: boolean;
  initiateGoogleCalendar: () => void;
}

interface GroupSectionProps {
  groupUserRoles: string[];
  groupIds: string[] | undefined;
  emailVerified: boolean;
  groupInvitation: GroupInvitation | null;
  handleInvitationResponse: (accept: boolean) => Promise<void>;
  loading: boolean;
  handleOpenNewGroupModal: () => void;
}

// Helper function to format dates (similar to admin-v2 formatting)
const formatDateTime = (dateString: string | Date | number | null | undefined): string => {
  if (!dateString) {
    return 'N/A';
  }
  let date: Date;
  if (typeof dateString === 'number') {
    // Assume Unix timestamp in seconds, convert to milliseconds
    date = new Date(dateString * 1000);
  } else {
    date = new Date(dateString);
  }
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export type UserDataType = {
  name?: string;
  institution?: string;
  email?: string;
  notes?: UserNotesDataType[];
};

interface GroupInvitation {
  group_id: string;
  group_name: string;
  invitation?: any; // You can specify more details if known
}

// Create an interface for the navigation section item
interface NavigationSection {
  id: string;
  label: string;
  icon: React.ReactElement;
  condition?: () => boolean;
  badge?: string;
}

// Placeholder for navigationSections - will be moved inside UserView

// Component to display clinician section (combined profile and provider info)
const ProfileSection = ({
  userInfo,
  emailVerified,
  resendVerificationEmail,
  resendingEmail,
  userPicture,
  isJotRxEnabled,
  isProxy,
}: ProfileSectionProps) => (
  <>
    <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Stack direction="column" spacing={2} sx={{ alignItems: 'center' }}>
        <Avatar src={userPicture} alt={userInfo?.name || 'User'} sx={{ width: 100, height: 100 }} />
        <Typography variant="h5">{userInfo?.name}</Typography>
        <Typography variant="body1">{userInfo?.institution}</Typography>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="body2">{userInfo?.email}</Typography>
          {!emailVerified && (
            <Button
              color="warning"
              size="small"
              variant="outlined"
              onClick={resendVerificationEmail}
              disabled={resendingEmail}
              startIcon={resendingEmail ? <CircularProgress size={16} /> : null}
            >
              Verify Email
            </Button>
          )}
        </Stack>
        {emailVerified && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Email has been verified
          </Alert>
        )}
      </Stack>
    </Card>
    <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Provider Information
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <ProviderInformationView />
    </Card>
    {isJotRxEnabled && (
      <div id="eprescription-user-section">
        <EPrescriptionUserSection />
        {!isProxy && <DEANumberManagement />}
      </div>
    )}
  </>
);

// Component for integrations section
const IntegrationsSection = ({
  loading,
  googleCalendarFeatureFlag,
  initiateGoogleCalendar,
}: IntegrationsSectionProps) => (
  <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
    <Typography variant="h6" gutterBottom>
      External Integrations
    </Typography>
    <Divider sx={{ mb: 2 }} />
    <Stack spacing={3}>
      {googleCalendarFeatureFlag && (
        <Box>
          <Typography variant="body1" gutterBottom>
            Connect your Google Calendar to sync appointments.
          </Typography>
          <Button
            variant="contained"
            onClick={initiateGoogleCalendar}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <CalendarMonth />}
          >
            {loading ? 'Connecting...' : 'Connect Google Calendar'}
          </Button>
        </Box>
      )}
    </Stack>
  </Card>
);


// Format subscription status for display
const formatSubscriptionStatus = (status: string | undefined | null): string => {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
};

// Get color for subscription status
const getSubscriptionStatusColor = (
  status: string | undefined | null,
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  if (!status) return 'default';
  const statusLower = status.toLowerCase();
  if (statusLower === 'active') return 'success';
  if (statusLower === 'trialing') return 'info';
  if (statusLower === 'canceled' || statusLower === 'cancelled') return 'error';
  if (statusLower === 'past_due') return 'warning';
  return 'default';
};

// Component for My Access section
const MyAccessSection = () => {
  const { currentTier, currentAddOns, entitlements, status, isLoading, error } = useMyAccess();
  const userRoles = useGroupUserRoles();
  const isBillingAdmin = userRoles.includes('billing_administrator');

  if (isLoading) {
    return (
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Alert severity="error">Unable to load access information. Please try again later.</Alert>
      </Card>
    );
  }

  return (
    <>
      {/* Subscription Status */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Subscription Status:
        </Typography>
        <Chip
          label={formatSubscriptionStatus(status)}
          color={getSubscriptionStatusColor(status)}
          size="small"
          sx={{ fontWeight: 500 }}
        />
      </Box>

      {/* Guidance Copy */}
      <Alert severity="info" icon={<Info />} sx={{ mb: 3 }}>
        {isBillingAdmin ? (
          <Typography variant="body2">
            To manage your subscription or update your plan, visit the{' '}
            <Typography
              component="a"
              href="/practice/subscriptions"
              variant="body2"
              sx={{ color: 'primary.main', textDecoration: 'underline' }}
            >
              Subscription page
            </Typography>
            .
          </Typography>
        ) : (
          <Typography variant="body2">
            <strong>Need to make changes?</strong> Contact your organization&apos;s practice
            administrator to update your subscription or make changes to your plan.
          </Typography>
        )}
      </Alert>

      {/* Tier Section */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Tier
      </Typography>
      <CurrentPlanCard tier={currentTier} />

      {/* Add-ons Section */}
      <AddOnsSection addOns={currentAddOns} />
      <EntitlementsSection entitlements={entitlements} />
    </>
  );
};

// Fix the type for the FeaturesSection component props
interface FeaturesSectionProps {
  ehrModeFeatureFlag: boolean;
  allowRevertLegacyFeatureFlag: boolean;
  toggleEhrMode: any;
}

// Component for features section
const FeaturesSection = ({
  ehrModeFeatureFlag,
  allowRevertLegacyFeatureFlag,
  toggleEhrMode,
}: FeaturesSectionProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const [isTogglingEhr, setIsTogglingEhr] = useState(false);

  const handleToggleEhrMode = async (enabled: boolean) => {
    setIsTogglingEhr(true);
    const result = await toggleEhrMode({ enabled });
    const success = 'data' in result && !('error' in result);
    if (success) {
      enqueueSnackbar(
        enabled ? "You're now using the new features!" : "You've switched back to legacy mode.",
        { variant: 'success' },
      );
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } else {
      enqueueSnackbar(
        `Failed to ${enabled ? 'enable' : 'disable'} features. Please try again later.`,
        { variant: 'error' },
      );
    }
    setIsTogglingEhr(false);
  };

  const isEhrModeEnabled = ehrModeFeatureFlag === true;

  return (
    <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Features
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {/* EHR Mode Section */}
      <Box sx={{ mb: 3 }}>
        {isEhrModeEnabled ? (
          <>
            <Typography variant="body1" gutterBottom>
              You currently have all of JotPsych's newest features!
            </Typography>
            {allowRevertLegacyFeatureFlag ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  You can temporarily switch back to legacy mode if needed, but we recommend staying
                  with enhanced features for the best experience.
                </Typography>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => handleToggleEhrMode(false)}
                  disabled={isTogglingEhr}
                  startIcon={isTogglingEhr ? <CircularProgress size={16} /> : <ToggleOn />}
                >
                  {isTogglingEhr ? 'Switching...' : 'Switch back to legacy mode'}
                </Button>
              </>
            ) : null}
          </>
        ) : (
          <>
            <Typography variant="body1" gutterBottom>
              Upgrade to JotPsych's enhanced features at no additional cost! Unlock multiple
              recordings per encounter, AI-assisted scheduling, enhanced patient profiles, and more!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              All new features are fully compatible with your existing data and workflow.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleToggleEhrMode(true)}
              disabled={isTogglingEhr}
              startIcon={isTogglingEhr ? <CircularProgress size={16} /> : <Lightbulb />}
            >
              {isTogglingEhr ? 'Enabling...' : 'Enable Enhanced Features'}
            </Button>
          </>
        )}
      </Box>

    </Card>
  );
};

function UserView() {
  const userInfo = useUserInfo();
  const emailVerified = useEmailVerified();
  const groupUserRoles = useGroupUserRoles();
  const groupIds = useGroupIds();
  const actionsFeatureFlag = useFeatureFlag('actions');
  const googleCalendarFeatureFlag = useFeatureFlag('google_calendar_sync');
  const ehrModeFeatureFlag = useFeatureFlag('ehr_mode');
  const allowRevertLegacyFeatureFlag = useFeatureFlag('allow_revert_legacy');
  const billingFeatureFlag = useFeatureFlag('billing_feature');
  const ehrExtensionRollout = useFeatureGate(FeatureGates.EHR_EXTENSION_ADDON);
  const { isBundlingEnabled: showBundling } = useBundlingEnabled();
  const { isAvailable: isJotMeetAvailable, isLoading: isJotMeetAvailabilityLoading } =
    useJotMeetAvailability();
  // GDN-1119: effective prepare-on-behalf capability (per-person flag AND the
  // operations_support role) — the backend's proxy authority. Not a role.
  const isProxy = useCanPrepareOnBehalf();
  const { entitlement: jotrxEntitlement } = useEntitlement('jotrx');
  // Prescribing proxies are non-billable and never hold a personal JotRx
  // entitlement. They reach E-Prescriptions on the strength of the org being
  // connected to DoseSpot (enforced inside EPrescriptionUserSection and the
  // setup-proxy backend). Prescribers still require their own entitlement.
  const isJotRxEnabled = isProxy ? true : !!jotrxEntitlement;
  const [toggleEhrMode] = useToggleEhrModeMutation();
  const { data: languagesAndPreferencesData } = useGetLanguagesAndPreferencesQuery(undefined);
  const [resendVerificationEmail] = useResendVerificationEmailMutation();
  const [getAdminEmails] = useLazyGetAdminEmailsQuery();
  const [createOrRetrieveCheckoutSession] = useCreateOrRetrieveCheckoutSessionMutation();
  const [createCustomerPortalSession] = useCreateCustomerPortalSessionMutation();
  const userPicture = useUserPicture();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();

  const { state: uiState, setState: setUIState } = useUIState();
  const { user } = useAuth0();
  const [activeSection, setActiveSection] = useState('clinician');
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [emailModalOpen, setEmailModalOpen] = useState<boolean>(false);
  const [emailList, setEmailList] = useState<AdminEmails[]>([]);
  // languagesAndPreferences now comes from the query
  const languagesAndPreferences = useMemo(() => {
    if (!languagesAndPreferencesData) return undefined;

    return {
      transcriptionLanguages: languagesAndPreferencesData.transcription_languages,
      userPreferences: languagesAndPreferencesData.user_preferences,
    } as LanguagesAndPreferences;
  }, [languagesAndPreferencesData]);

  const [loading, setLoading] = useState<boolean>(false);

  const [resendingEmail, setResendingEmail] = useState<boolean>(false);

  const win: Window = window;

  // Navigation sections
  const navigationSections: NavigationSection[] = [
    { id: 'clinician', label: 'Clinician', icon: <AccountCircle /> },
    { id: 'preferences', label: 'Languages', icon: <Settings /> },
    {
      id: 'features',
      label: 'Features',
      icon: <Lightbulb />,
    },
    {
      id: 'document-preferences',
      label: 'Document Generation',
      icon: <Description />,
    },
    {
      id: 'recording-preferences',
      label: 'Recording',
      icon: <Mic />,
    },
    {
      id: 'copy-paste',
      label: 'Copy/Paste',
      icon: <ContentCopy />,
    },
    {
      id: 'telehealth',
      label: 'Telehealth',
      icon: <Videocam />,
      condition: () => isJotMeetAvailabilityLoading || isJotMeetAvailable,
    },
    {
      id: 'integrations',
      label: 'Integrations',
      icon: <IntegrationInstructions />,
      badge: 'NEW',
      condition: () => ehrExtensionRollout,
    },
    { id: 'referral', label: 'Referral Program', icon: <Redeem /> },
    {
      id: 'my-access',
      label: 'My Access',
      icon: <Security />,
      condition: () => showBundling,
    },
    {
      id: 'diagnostics',
      label: 'Diagnostics',
      icon: <BugReport />,
    },
  ];

  // Handle hash navigation
  useEffect(() => {
    const hash = location.hash.slice(1); // Remove the # character
    if (hash && navigationSections.some(section => section.id === hash)) {
      setActiveSection(hash);
    }
  }, [location.hash]);

  const fetchAdminEmails = async () => {
    try {
      const result = await getAdminEmails(undefined).unwrap();
      setEmailList(result.admin_emails);
    } catch (error) {
      console.error('Failed to fetch admin emails:', error);
    }
  };

  // function to send verification email
  const handleResendVerificationEmail = async () => {
    setResendingEmail(true);

    try {
      await resendVerificationEmail({}).unwrap();
      enqueueSnackbar('Verification email sent!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('An error occurred while sending verification email.', {
        variant: 'error',
      });
    } finally {
      setResendingEmail(false);
    }
  };

  // fetchLanguagesAndPreferences removed - now using RTK Query

  const handleOpenEmailModal = () => {
    fetchAdminEmails();
    setEmailModalOpen(true);
  };

  useEffect(() => {
    if (user) {
      const roles = user['https://api.smartscribe.health/roles'];
      if (roles) {
        if (roles.includes('Admin') || roles.includes('Moderator')) {
          setIsAdmin(true);
        }
      }
    }
  }, [user]);

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setUIState({ ...uiState, navbar: { showCopyNotification: true } });
    });
  };

  // useEffect for fetchLanguagesAndPreferences removed - RTK Query handles it

  const handleNavItemClick = (id: string) => {
    if (id === 'notes') {
      navigate('/notes');
    } else if (id === 'admin' && isAdmin) {
      navigate('/admin');
    } else {
      setActiveSection(id);
      // Update URL hash without triggering navigation
      window.history.replaceState(null, '', `#${id}`);
    }
  };

  const getContentForSection = (section: string) => {
    switch (section) {
      case 'clinician':
        return (
          <ProfileSection
            userInfo={userInfo}
            emailVerified={emailVerified || false}
            resendVerificationEmail={handleResendVerificationEmail}
            resendingEmail={resendingEmail}
            userPicture={userPicture}
            isJotRxEnabled={isJotRxEnabled}
            isProxy={isProxy}
          />
        );
      case 'preferences':
        return (
          <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Languages
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <UserPreferencesForm
              onSuccessfulFormSubmission={() => {
                enqueueSnackbar('Preferences successfully updated!', {
                  variant: 'success',
                });
              }}
              languagesAndPreferences={languagesAndPreferences}
            />
          </Card>
        );
      case 'features':
        return (
          <FeaturesSection
            ehrModeFeatureFlag={ehrModeFeatureFlag}
            allowRevertLegacyFeatureFlag={allowRevertLegacyFeatureFlag}
            toggleEhrMode={toggleEhrMode}
          />
        );
      case 'document-preferences':
        return (
          <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Document Generation
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <DocumentPreferencesForm
              onSuccessfulFormSubmission={() => {
                enqueueSnackbar('Document preferences saved successfully!', {
                  variant: 'success',
                });
              }}
            />
          </Card>
        );
      case 'copy-paste':
        return (
          <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Copy/Paste
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <CopyPastePreferencesForm
              onSuccessfulFormSubmission={() => {
                enqueueSnackbar('Copy/paste preferences saved successfully!', {
                  variant: 'success',
                });
              }}
            />
          </Card>
        );
      case 'recording-preferences':
        return (
          <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recording
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <RecordingPreferencesForm
              onSuccessfulFormSubmission={() => {
                enqueueSnackbar('Recording preferences saved successfully!', {
                  variant: 'success',
                });
              }}
            />
          </Card>
        );
      case 'telehealth':
        return (
          <JotMeetSettingsSection
            onOpenClinicianSettings={() => handleNavItemClick('clinician')}
            onCopySuccess={() =>
              enqueueSnackbar('Waiting room link copied.', { variant: 'success' })
            }
            onCopyError={() =>
              enqueueSnackbar('Unable to copy waiting room link. Please try again.', {
                variant: 'error',
              })
            }
          />
        );
      case 'integrations':
        return (
          <Stack spacing={4}>
            <EhrExtensionCard />
            {googleCalendarFeatureFlag && (
              <IntegrationsSection
                loading={loading}
                googleCalendarFeatureFlag={googleCalendarFeatureFlag}
                initiateGoogleCalendar={() => {
                  setLoading(true);
                  initiateGoogleIntegration()
                    .catch(error => {
                      console.error('Failed to initiate Google Calendar auth:', error);
                      enqueueSnackbar('Failed to connect to Google Calendar. Please try again.', {
                        variant: 'error',
                      });
                    })
                    .finally(() => {
                      setLoading(false);
                    });
                }}
              />
            )}
          </Stack>
        );
      case 'referral':
        return <ReferralDashboard />;
      case 'my-access':
        return <MyAccessSection />;
      case 'diagnostics':
        return (
          <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              System Diagnostics
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              View system performance metrics, debug information, and troubleshooting tools.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/diagnostics')}
              startIcon={<BugReport />}
            >
              Open Diagnostics Page
            </Button>
          </Card>
        );
      default:
        return (
          <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography>Select a section from the menu</Typography>
          </Card>
        );
    }
  };

  // if user info hasn't been input yet, show form instead
  if (userInfo && !userInfo.name) {
    return (
      <Box>
        <UserInfoForm />
        <LogoutButton />
      </Box>
    );
  }

  return (
    <LayoutWrapper>
      <LayoutWrapper.TopBar>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            px: 2,
          }}
        >
          <Typography variant="h6">Account Settings</Typography>
          <LogoutButton />
        </Box>
      </LayoutWrapper.TopBar>

      <LayoutWrapper.MainContent>
        {userInfo && (
          <Box
            sx={{
              maxWidth: 1200,
              mx: 'auto',
              px: { xs: 1, sm: 2, md: 3 },
              py: 3,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
            }}
          >
            {/* Side Navigation */}
            <Box
              sx={{
                width: { xs: '100%', md: 250 },
                mb: { xs: 2, md: 0 },
              }}
            >
              {isMobile ? (
                // Mobile tabs
                <Tabs
                  value={activeSection}
                  onChange={(_, value) => handleNavItemClick(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 2 }}
                >
                  {navigationSections
                    .filter(section => !section.condition || section.condition())
                    .map(section => (
                      <Tab
                        key={section.id}
                        value={section.id}
                        label={
                          section.badge ? (
                            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                              <span>{section.label}</span>
                              <Chip
                                label={section.badge}
                                size="small"
                                color="primary"
                                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
                              />
                            </Stack>
                          ) : (
                            section.label
                          )
                        }
                        icon={section.icon}
                        iconPosition="start"
                      />
                    ))}
                  {isAdmin && (
                    <Tab value="admin" label="Admin Panel" icon={<Shield />} iconPosition="start" />
                  )}
                </Tabs>
              ) : (
                // Desktop sidebar
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    borderRadius: 1,
                    height: '100%',
                    overflow: 'hidden',
                  }}
                >
                  <List disablePadding>
                    {navigationSections
                      .filter(section => !section.condition || section.condition())
                      .map(section => (
                        <ListItem
                          key={section.id}
                          disablePadding
                          sx={{
                            backgroundColor:
                              activeSection === section.id ? 'action.selected' : 'transparent',
                          }}
                        >
                          <ListItemButton
                            onClick={() => handleNavItemClick(section.id)}
                            selected={activeSection === section.id}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>{section.icon}</ListItemIcon>
                            <ListItemText primary={section.label} />
                            {section.badge && (
                              <Chip
                                label={section.badge}
                                size="small"
                                color="primary"
                                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                              />
                            )}
                          </ListItemButton>
                        </ListItem>
                      ))}
                    {isAdmin && (
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => handleNavItemClick('admin')}>
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Shield />
                          </ListItemIcon>
                          <ListItemText primary="Admin Panel" />
                        </ListItemButton>
                      </ListItem>
                    )}
                  </List>
                </Paper>
              )}
            </Box>

            {/* Content Area */}
            <Box
              sx={{
                flexGrow: 1,
                width: { xs: '100%', md: 'calc(100% - 250px - 24px)' }, // adjust for navigation width and gap
              }}
            >
              {/* JotRx Setup Callout - only on Clinician tab */}
              {activeSection === 'clinician' && (
                <JotRxSetupCallout page="account-settings" />
              )}
              {/* Dynamic content based on selected section */}
              {getContentForSection(activeSection)}
            </Box>
          </Box>
        )}
        {/* Modals */}
        <EmailModal
          emailList={emailList}
          emailModalOpen={emailModalOpen}
          setEmailModalOpen={setEmailModalOpen}
          fetchAdminEmails={fetchAdminEmails}
        />
      </LayoutWrapper.MainContent>
    </LayoutWrapper>
  );
}

export default UserView;
